import * as React from 'react';
import {Component} from 'react';
import List, {ListProps} from '../../../../components/Common/List';
import {SearchInput, SearchInputProps} from './SearchInput';
import {SearchDropdown} from './SearchDropdown';
import {getLocalizedText, texts} from '../../../../constants/TextConst';
import {Dropdown, PriorityIcon} from '@priority-software/priority-style-react';
import {highlightText} from '../../../library/utils/highlightText';
import {Substract} from '../../../../types';
import {MenuItem} from '../Menus/MenuItem';
import {Label} from '../../../../components/Common/Label';

interface SearchProps<Option> {
    dropdown?: boolean;
    options: Array<Option>;
    isOptionDisabled?(option: Option): boolean;
    onSelect?(option: Option): void;
    /** use to specify whether dropdown should close on item select. default: true */
    closeOnSelect?: boolean;
    keyToSearch: string;
    /** options are displayed when search input gets focused, before start typing. default: false, only for dropdown */
    openOnFocus?: boolean;
    /** customize searchbar input properties */
    inputProps?: SearchInputProps;
    /** customize List propeties */
    listProps?: Substract<ListProps<Option>, 'data'>;
    /** customize List renderer */
    renderList?: (props: ListProps<Option>) => JSX.Element;
}

export class Search<P> extends Component<SearchProps<P>, {search: string}> {
    public static ofType<T>() {
        return Search as new (props: SearchProps<T>) => Search<T>;
    }

    constructor(props) {
        super(props);
        this.state = {
            search: ''
        };
    }

    handleSearchChange = value => {
        this.setState({search: value});
    };

    handleOptionSelect = option => {
        this.props.onSelect(option);
        this.clearSearch();
    };

    createFilter(option, search, keyToSearch) {
        return option[keyToSearch].toLowerCase().includes(search.toLowerCase());
    }

    clearSearch() {
        this.setState({search: ''});
    }

    renderDropdownItem = ({item, index, key}) => {
        const {onSelect, isOptionDisabled, closeOnSelect, options} = this.props;
        return (
            <Dropdown.Item
                key={key}
                index={index}
                divider={options && index !== options.length - 1}
                onClick={() => onSelect(item)}
                closeOnClick={closeOnSelect}
                disabled={isOptionDisabled && isOptionDisabled(item)}
            >
                <MenuItem title={highlightText(item.title, this.state.search)} />
            </Dropdown.Item>
        );
    };

    render() {
        const {
            options,
            keyToSearch,
            dropdown,
            listProps,
            inputProps,
            renderList: CustomList
        } = this.props;
        const {search} = this.state;
        const filteredOptions =
            search !== ''
                ? options.filter(option =>
                      this.createFilter(option, this.state.search, keyToSearch)
                  )
                : options;
        if (dropdown) {
            return (
                <SearchDropdown
                    {...this.props}
                    inputProps={{
                        value: search,
                        onChange: this.handleSearchChange,
                        ...inputProps
                    }}
                >
                    <Dropdown.Content>
                        <Dropdown.Section gradient>
                            {filteredOptions.length > 0 ? (
                                <List
                                    autoHeight
                                    {...listProps}
                                    data={filteredOptions}
                                    renderItem={this.renderDropdownItem}
                                />
                            ) : (
                                <Dropdown.Item key="no-results">
                                    <Label textColor="secondary-400" skin="paragraph-2">
                                        {getLocalizedText(texts.noSearchResults)}
                                    </Label>
                                </Dropdown.Item>
                            )}
                        </Dropdown.Section>
                    </Dropdown.Content>
                </SearchDropdown>
            );
        }
        return (
            <React.Fragment>
                <SearchInput
                    value={search}
                    onChange={this.handleSearchChange}
                    icon={PriorityIcon.searchThin}
                    {...inputProps}
                />
                {CustomList ? (
                    <CustomList {...listProps} data={filteredOptions} />
                ) : (
                    <List autoHeight {...listProps} data={filteredOptions} />
                )}
            </React.Fragment>
        );
    }
}
